import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import useSheetSwipe from '../../hooks/useSheetSwipe';
import Button from './Button.jsx';

/**
 * Kanonisches Modal des Design-Systems (siehe MODERNIZATION_PLAN.md P1.1).
 *
 * Basiert auf dem nativen <dialog>-Element: showModal() liefert Focus-Trap,
 * Escape-Handling und Top-Layer (kein z-index-Gefrickel) vom Browser gratis.
 *
 * - Mobile: Bottom-Sheet (rundet oben, Safe-Area unten), Desktop: zentriert
 * - Mobile + fullscreenOnMobile: Vollbild wie eine eigene Seite (fuer Formulare)
 * - Backdrop-Klick + Escape schließen kontrolliert über onClose
 * - Nur Token-Klassen; Backdrop bg-black/60 ist dokumentierte Ausnahme (TOKENS.md)
 *
 * Props:
 * - open, onClose (Pflicht)
 * - title: rendert Header mit h2 (aria-labelledby); ohne title ariaLabel setzen!
 * - footer: Aktionsleiste (Buttons), auf Mobile gestapelt (primäre Aktion oben)
 * - size: 'sm' | 'md' | 'lg' | 'full'
 * - fullscreenOnMobile: auf dem Telefon Vollbild statt Bottom-Sheet — Pflicht fuer
 *   alles mit Eingabefeldern (siehe Kommentar an SHELL_FULLSCREEN)
 * - hideClose: X-Button ausblenden (z.B. wenn der Inhalt eigene Aktionen hat)
 * - swipeToClose: Bottom-Sheet auf dem Telefon nach unten wegwischen (mit Haptik,
 *   hooks/useSheetSwipe). Ohne title faellt dort die Kopfzeile mit dem X weg —
 *   Greifer, Wischen und Schleier reichen, und Inhalte mit eigenen Knoepfen in
 *   der oberen Ecke (Profilkarte) stossen nicht mehr an das X. Fuer Screenreader
 *   bleibt der Knopf erhalten (sr-only), am Desktop sichtbar wie bisher.
 */

const SIZES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
  full: 'sm:max-w-[calc(100vw-4rem)] sm:h-[90vh]',
};

// Ab Desktop-Breite ist beide Male dasselbe zu sehen: eine zentrierte Karte.
// Deshalb stehen die sm:-Klassen nur einmal hier, und die beiden Mobile-Varianten
// darunter fassen nichts an, was der Desktop wieder zurechtruecken muesste.
const SHELL_DESKTOP = 'sm:inset-0 sm:m-auto sm:w-full sm:rounded-3xl sm:max-h-[85vh] sm:pb-0 sm:animate-fade-in';

// Mobile-Standard: Blatt, das von unten hereinfaehrt.
// 75vh statt 85: Ein Sheet, das fast bis an die Oberkante reicht, wirkt wie eine
// misslungene Vollbildseite. Der sichtbare Rest des Hintergrunds haelt den Bezug
// zur Seite darunter.
//
// Position ausdruecklich unten verankert: Ein per showModal() geoeffnetes <dialog>
// liegt fix mit inset:0 im Top-Layer, und ob die automatischen Margins es nach
// unten schieben, haengt an der Reihenfolge der Margin-Klassen. Mit top:auto +
// bottom:0 ist die Verankerung eindeutig, egal wie Tailwind sortiert.
const SHELL_SHEET = [
  'm-0 w-full max-w-none rounded-t-3xl rounded-b-none',
  'fixed inset-x-0 bottom-0 top-auto',
  'max-h-[75vh]',
  'pb-safe-bottom',
].join(' ');

// Mobile-Vollbild: das Blatt wird zur Seite.
// Grund: Ein Bottom-Sheet mit 75vh laesst fuer ein Formular kaum Platz — und
// sobald die Tastatur aufgeht, steht vom Formular praktisch nur noch die
// Ueberschrift ueber den Knoepfen (so gesehen im Logbuch-Formular auf dem iPhone).
// Eine Vollbildseite behaelt die Felder oben und laesst den Inhalt darunter scrollen.
//
// Durchgehend `max-sm:`, nicht unpraefixiert: Diese Klassen gelten NUR unterhalb
// von sm und geraten der Desktop-Karte damit gar nicht erst in die Quere — sonst
// muesste jede davon per sm: zurueckgenommen werden, und `sm:h-auto` gegen
// SIZES.full ('sm:h-[90vh]') waere ein Sortier-Gluecksspiel.
//
// `h-full` ist Pflicht, nicht Kosmetik: Das UA-Stylesheet gibt jedem <dialog>
// `height: fit-content`. Ohne Hoehen-Klasse dehnt `inset-0` das Element deshalb
// NICHT auf den Viewport, sondern der Inhalt bestimmt die Hoehe — ein langes
// Formular ragte unten aus dem Schirm heraus (gemessen: 957px bei 844px Viewport).
//
// KEIN Top-Layer auf dem Telefon (September 2026): Das Vollbild wird mit
// dialog.show() geoeffnet, nicht mit showModal(). Auf dem iPhone (Capacitor,
// Screenshot vom 7.9.2026) begann der per showModal() geoeffnete Dialog erst
// ~88pt unter der Oberkante, mit ~0 Polsterung oben — der Abdunkelungs-Schleier
// (::backdrop) deckte den Schirm dagegen komplett ab. WebKit misst die Ebene des
// Top-Layers in der WKWebView also anders als den normalen Viewport. Die uebrigen
// Vollbild-Overlays der App (ImageCropModal, WelcomeTour) sind gewoehnliche
// `fixed inset-0`-Container direkt am <body> und reichen nachweislich bis unter
// die Statusleiste — genau dorthin zieht das Vollbild-Modal um: per Portal am
// <body> (root stacking context), z-[100] ueber der Bottom-Navigation (z-50).
// Was das Top-Layer mitbrachte, ersetzt der Rest der Komponente: Escape ueber
// onKeyDown, Fokus auf die Ueberschrift, aria-modal, Scroll-Sperre am body.
// Ein ::backdrop braucht ein Vollbild nicht.
const SHELL_FULLSCREEN = [
  'max-sm:m-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-none',
  'max-sm:fixed max-sm:inset-0 max-sm:z-[100]',
  'max-sm:h-full max-sm:max-h-none',
  // Statusleiste/Home-Indikator freihalten — der Dialog selbst hat p-0,
  // die Polsterung liegt also sauber vor Header und Footer.
  'max-sm:pt-safe-top max-sm:pb-safe-bottom',
].join(' ');

// Unterhalb von sm gilt SHELL_FULLSCREEN — dieselbe Grenze wie Tailwinds `max-sm:`.
const PHONE_QUERY = '(max-width: 639.98px)';
const isPhoneViewport = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(PHONE_QUERY).matches;

const Modal = ({
  open,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  size = 'md',
  fullscreenOnMobile = false,
  hideClose = false,
  swipeToClose = false,
  className = '',
  // Inhalte, die ihr eigenes Layout mitbringen (farbiger Kopf, randlose Bilder),
  // setzen hier 'p-0' statt gegen das Standard-Padding anzukämpfen.
  bodyClassName = 'p-5',
}) => {
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  // true = per showModal() im Top-Layer (Bottom-Sheet, Desktop-Karte);
  // false = Vollbild auf dem Telefon per show() — siehe SHELL_FULLSCREEN.
  const inTopLayerRef = useRef(true);
  const titleId = React.useId();
  useBodyScrollLock(open);
  // Nur das Blatt laesst sich wegwischen; ein Vollbild ist eine Seite, keine Karte.
  const swipeEnabled = open && swipeToClose && !fullscreenOnMobile;
  useSheetSwipe(dialogRef, { enabled: swipeEnabled, onClose, isActive: isPhoneViewport });
  // Kopfzeile nur fuer das X? Dann auf dem Telefon unsichtbar (siehe swipeToClose).
  const headerSrOnlyOnPhone = swipeEnabled && !title;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      const phoneFullscreen = fullscreenOnMobile && isPhoneViewport();
      inTopLayerRef.current = !phoneFullscreen;
      // jsdom (Tests) kennt show()/showModal() nicht → Fallback auf offenes Attribut
      if (phoneFullscreen && typeof dialog.show === 'function') dialog.show();
      else if (!phoneFullscreen && typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
  }, [open, fullscreenOnMobile]);

  // Fokus auf die Ueberschrift NICHT ueber das native `autofocus`-Attribut,
  // sondern hier per .focus({ preventScroll: true }): Ohne preventScroll
  // scrollt WebKit beim Fokussieren "ins Bild" und verschiebt dabei den
  // scrollenden Body-Bereich (SHELL_SHEET) unter den Header — die erste
  // Zeile des Inhalts landete dadurch fuer den Nutzer unsichtbar (gemeldet
  // am "Session verlassen?"-Dialog: Satzanfang fehlte, Rest war sichtbar).
  useEffect(() => {
    if (open && titleRef.current) {
      titleRef.current.focus({ preventScroll: true });
    }
  }, [open]);

  if (!open) return null;

  // Escape feuert das native 'cancel' — abfangen, damit der State führt
  const handleCancel = (e) => {
    e.preventDefault();
    onClose();
  };

  // Klicks auf ::backdrop landen auf dem <dialog>-Element selbst. Ohne Top-Layer
  // (Vollbild am Telefon) gibt es keinen Backdrop — ein Tipp auf die eigene
  // Polsterung (Safe-Area-Streifen) darf das Formular dann nicht schliessen.
  const handleClick = (e) => {
    if (inTopLayerRef.current && e.target === dialogRef.current) onClose();
  };

  // Ausserhalb des Top-Layers feuert der Browser bei Escape kein 'cancel' —
  // deshalb hier von Hand, damit sich das Vollbild auch mit Tastatur schliesst.
  const handleKeyDown = (e) => {
    if (!inTopLayerRef.current && e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const dialogElement = (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={!title ? ariaLabel : undefined}
      className={[
        fullscreenOnMobile ? SHELL_FULLSCREEN : SHELL_SHEET,
        SHELL_DESKTOP,
        SIZES[size] || SIZES.md,
        'bg-surface text-foreground border-0 p-0 shadow-token-xl',
        'flex flex-col',
        'backdrop:bg-black/60 backdrop:backdrop-blur-sm',
        fullscreenOnMobile ? 'animate-fade-in' : 'animate-slide-up',
        'motion-reduce:animate-none',
        className,
      ].join(' ')}
    >
      {/* Greifer wie bei iOS-Sheets — macht ohne Worte klar, dass das Blatt von
          unten kommt und wieder verschwindet. Am Desktop (zentrierte Karte) und im
          Vollbild (da kommt nichts von unten) fehl am Platz. */}
      {!fullscreenOnMobile && (
        <div className="flex justify-center pt-2.5 sm:hidden" aria-hidden="true">
          <span className="h-1 w-9 rounded-full bg-border" />
        </div>
      )}

      {(title || !hideClose) && (
        /* Vollbild auf dem Telefon: Kopf nur 8px unter die Statusleiste, nicht 20px.
           Die Safe-Area (pt-safe-top, ~60px auf iPhones mit Dynamic Island) plus
           20px Polster ergaben einen leeren weissen Streifen von gut 80px ueber
           der Ueberschrift — das Formular wirkte "nach unten gedrueckt". Native
           iOS-Sheets setzen den Titel direkt unter die Statusleiste. */
        <div className={`flex items-start justify-between gap-4 p-5 pb-0 ${fullscreenOnMobile ? 'max-sm:pt-2' : ''} ${headerSrOnlyOnPhone ? 'max-sm:sr-only' : ''}`}>
          {title ? (
            /* Der Fokus landet beim Oeffnen auf der Ueberschrift statt auf dem
               Schliessen-Knopf: Screenreader lesen zuerst, worum es geht, und es
               liegt kein Fokusring auf einem Knopf, den niemand drücken wollte.
               Kein natives `autoFocus` — siehe titleRef-Effekt oben (preventScroll). */
            <h2 ref={titleRef} id={titleId} tabIndex={-1} className="text-lg font-bold text-foreground outline-none">
              {title}
            </h2>
          ) : (
            <span aria-hidden="true" />
          )}
          {!hideClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="-m-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl p-2 text-muted-foreground transition-colors duration-base hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* `min-h-0` ist Pflicht, nicht Kosmetik: Ohne min-h-0 darf ein
          Flex-Kind nicht kleiner werden als sein Inhalt — der Bereich waechst dann
          ueber die Modal-Hoehe hinaus und schiebt den Footer aus dem Dialog, statt
          selbst zu scrollen. Genau so lagen die Aktionsknoepfe ueber dem Formular.

          `flex-auto` (flex: 1 1 auto) statt `flex-1` (flex: 1 1 0%) — der
          Unterschied ist die flex-basis, und daran hing das kaputte Bottom-Sheet:
          Das Sheet (SHELL_SHEET) hat KEINE Hoehen-Klasse, gilt also mit dem
          UA-Stylesheet als `height: fit-content` — die Dialoghoehe wird aus dem
          Inhalt errechnet. Mit basis 0 steuert dieser Bereich dazu nur sein
          Padding bei, nicht seinen Text: WebKit (iOS-WebView, Safari) macht das
          Sheet dann exakt Header + 2x20px + Footer hoch, und der eigentliche
          Inhalt scrollt in einem 40px-Schlitz (gemessen: bodyH 40px bei 102px
          Text — genau der gemeldete Streifen am unteren Bildrand). Mit basis auto
          zaehlt der Inhalt mit, das Sheet waechst bis max-h-[75vh] und scrollt
          erst danach. Chromium rechnet in beiden Varianten gleich, der Fullscreen-
          Pfad ebenfalls (der hat mit `h-full` eine feste Hoehe) — deshalb fiel es
          nur im Bottom-Sheet auf dem iPhone auf.

          `[overflow-anchor:none]` ist ebenfalls Pflicht: Unsere Web-Fonts laden mit
          font-display:swap (public/fonts/fonts.css) — der Text steht zuerst in der
          Systemschrift und bricht kurz danach beim Wechsel auf Poppins neu um (z.B.
          2 -> 3 Zeilen bei kurzen Confirm-Texten). Der Browser haelt per Scroll
          Anchoring reflexhaft die zuletzt sichtbare Position fest und verschiebt
          dafuer den Scroll-Offset dieses Containers — dadurch rutschte die neu
          hinzugekommene erste Zeile aus dem sichtbaren Bereich (gemeldet am
          "Session verlassen?"-Dialog: Satzanfang fehlte, Rest war sichtbar). */}
      <div className={`flex-auto min-h-0 overflow-y-auto [overflow-anchor:none] ${bodyClassName}`}>{children}</div>

      {footer && (
        <div className="flex flex-col-reverse gap-3 border-t border-border p-5 sm:flex-row sm:justify-end">
          {footer}
        </div>
      )}
    </dialog>
  );

  // Portal an den <body>: Im Top-Layer waere das egal, ohne Top-Layer (Vollbild
  // am Telefon) entscheidet es — ein `fixed` innerhalb eines transformierten oder
  // gefilterten Vorfahren (framer-motion-Seitenwechsel) wuerde sich an diesem
  // Vorfahren ausrichten statt am Bildschirm, und ein z-index gilt nur innerhalb
  // dessen Stacking-Kontext.
  return typeof document !== 'undefined' ? createPortal(dialogElement, document.body) : dialogElement;
};

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  ariaLabel: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'full']),
  fullscreenOnMobile: PropTypes.bool,
  hideClose: PropTypes.bool,
  swipeToClose: PropTypes.bool,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
};

/**
 * Bestätigungsdialog: Modal.Confirm
 * variant="danger" für destruktive Aktionen (Löschen etc.)
 */
const Confirm = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'primary',
  busy = false,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    title={title}
    size="sm"
    footer={
      <>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={busy}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    {children}
  </Modal>
);

Confirm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'danger', 'success']),
  busy: PropTypes.bool,
};

Modal.Confirm = Confirm;

export default Modal;
